<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCountryOptions } from 'dashboard/composables/useCountryOptions';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// The bots of a marketplace inbox's countries, { de: botId }, keyed by lowercase ISO 3166
// alpha-2 code as the inbox stores them. Each one takes the conversations of its country
// instead of the inbox's default bot.
const props = defineProps({
  countryBots: {
    type: Object,
    default: () => ({}),
  },
  agentBots: {
    type: Array,
    default: () => [],
  },
  // Bots by country only replace the default bot, so they wait for one to be chosen.
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const { countryOptionsFor } = useCountryOptions();

let lastRowId = 0;
const toRows = countryBots =>
  Object.entries(countryBots || {}).map(([country, botId]) => {
    lastRowId += 1;
    return { id: lastRowId, country, botId: botId || '' };
  });
// A row without a country or a bot yet stays here and is not saved.
const toCountryBots = list =>
  Object.fromEntries(
    list
      .filter(row => row.country && row.botId)
      .map(row => [row.country, row.botId])
  );
const sameCountryBots = (a, b) => {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => Number(a[key]) === Number(b[key]))
  );
};

const rows = ref(toRows(props.countryBots));

// Rebuild on an outside change (another inbox loaded), not on our own update coming back.
watch(
  () => props.countryBots,
  value => {
    if (!sameCountryBots(value || {}, toCountryBots(rows.value))) {
      rows.value = toRows(value);
    }
  }
);

const emitUpdate = () => emit('update', toCountryBots(rows.value));

const optionsFor = row => countryOptionsFor(rows.value, row);

const botLabel = bot =>
  bot.description
    ? t('AGENT_BOTS.BOT_CONFIGURATION.OPTION', {
        name: bot.name,
        description: bot.description,
      })
    : bot.name;

const addRow = () => {
  lastRowId += 1;
  rows.value.push({ id: lastRowId, country: '', botId: '' });
};

const removeRow = row => {
  rows.value = rows.value.filter(other => other !== row);
  emitUpdate();
};

const setCountry = (row, country) => {
  row.country = country;
  emitUpdate();
};

const setBot = (row, botId) => {
  row.botId = botId ? Number(botId) : '';
  emitUpdate();
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <p v-if="disabled" class="mb-0 text-sm text-n-slate-11">
      {{ t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.DEFAULT_REQUIRED') }}
    </p>
    <div
      v-for="row in rows"
      :key="row.id"
      class="flex items-center gap-3"
      data-test-id="country-bot-row"
    >
      <ComboBox
        :model-value="row.country"
        :options="optionsFor(row)"
        :placeholder="t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.COUNTRY')"
        :disabled="disabled"
        class="flex-1"
        @update:model-value="country => setCountry(row, country)"
      />
      <select
        :value="row.botId"
        :disabled="disabled"
        class="flex-1 min-w-0 !mb-0"
        @change="event => setBot(row, event.target.value)"
      >
        <option value="" disabled>
          {{ t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.BOT') }}
        </option>
        <option v-for="bot in agentBots" :key="bot.id" :value="bot.id">
          {{ botLabel(bot) }}
        </option>
      </select>
      <button
        type="button"
        class="shrink-0 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
        :disabled="disabled"
        @click="removeRow(row)"
      >
        {{ t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.REMOVE') }}
      </button>
    </div>
    <div>
      <NextButton
        type="button"
        icon="i-lucide-plus"
        faded
        blue
        size="sm"
        :disabled="disabled"
        :label="t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.ADD')"
        @click="addRow"
      />
    </div>
  </div>
</template>
